import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, FileText, Calendar, Sparkles } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { exportCSV, exportPDF, exportBackup } from '@/utils/export';
import {
  exportAllData,
  importAllData,
  stripJournalMediaFromEntriesMap,
  getEntries,
  formatDate,
  formatShortDate,
  getEntry,
  saveEntry,
} from '@/utils/storage';
import { syncJournalEntriesToCloudBulk } from '@/utils/journalCloudSync';
import { seedSummaryHierarchyFromEntries } from '@/utils/aiCompanion';
import { toast } from 'sonner';

const normalizeDateToken = (rawDate) => {
  const trimmed = String(rawDate || '').trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const slash = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (!slash) return null;
  const day = Number(slash[1]);
  const month = Number(slash[2]);
  let year = Number(slash[3]);
  if (year < 100) year += 2000;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;

  const normalized = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  const parsed = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return normalized;
};

const splitImportedTextIntoEntries = (rawText) => {
  const lines = String(rawText || '').split(/\r?\n/);
  const entries = [];
  let currentDate = null;
  let buffer = [];

  const flush = () => {
    if (!currentDate) return;
    const text = buffer.join('\n').trim();
    if (text) {
      entries.push({ date: currentDate, text });
    }
    buffer = [];
  };

  for (const line of lines) {
    const lineTrim = line.trim();
    const headingDate = normalizeDateToken(lineTrim.replace(/:$/, ''));

    if (headingDate) {
      flush();
      currentDate = headingDate;
      continue;
    }

    const inlineDate = lineTrim.match(/^(\d{4}-\d{2}-\d{2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})\s*[:\-]\s*(.+)$/);
    if (inlineDate) {
      flush();
      currentDate = normalizeDateToken(inlineDate[1]);
      buffer.push(inlineDate[2]);
      continue;
    }

    if (currentDate) {
      buffer.push(line);
    }
  }

  flush();
  return entries;
};

const saveImportedEntries = (entries) => {
  entries.forEach(({ date, text }) => {
    const existing = getEntry(date);
    const previousNotes = existing?.answer5 ? `${existing.answer5}\n\n` : '';

    saveEntry(date, {
      goals: existing?.goals || {},
      answer1: existing?.answer1 || '',
      answer2: existing?.answer2 || '',
      answer3: existing?.answer3 || '',
      answer4: existing?.answer4 || '',
      answer5: `${previousNotes}${text}`,
      comments: existing?.comments || [],
    });
  });
};

const isDateKey = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));

/** RE-BUILD-ME-PRO full backup (export from this app). */
const isRebuildMeNativeBackup = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (typeof data.backupFormatVersion === 'number' && data.backupFormatVersion >= 1) {
    return typeof data.entries === 'object' && data.entries !== null && !Array.isArray(data.entries);
  }
  if (data.version === 3 && typeof data.exportedAt === 'string') {
    return typeof data.entries === 'object' && data.entries !== null && !Array.isArray(data.entries);
  }
  return false;
};

const getPostTimestampRaw = (o) => {
  if (!o || typeof o !== 'object') return null;
  const keys = ['timestamp', 'taken_at', 'creation_timestamp', 'created_at', 'time', 'timestamp_ms'];
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
  }
  return null;
};

const getPostCaptionText = (o) => {
  if (!o || typeof o !== 'object') return '';
  const cap = String(o.caption ?? o.title ?? o.text ?? o.body ?? o.comment ?? '').trim();
  return cap;
};

const isLikelyMediaOnlyPost = (o) => {
  const cap = getPostCaptionText(o);
  if (cap) return false;
  const uri = String(o.uri ?? o.url ?? o.media_url ?? '').toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|heic|bmp|mp4|mov|webm|m4v)(\?|#|$)/i.test(uri)) return true;
  if (o.media_type != null || o.mediaType != null) return true;
  if (o.media === true || o.is_video === true || o.isVideo === true) return true;
  return false;
};

const timestampToIsoDate = (rawTs) => {
  const n = Number(rawTs);
  if (!Number.isFinite(n)) return null;
  const ms = n > 1e12 ? n : n * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(d);
};

const arrayLooksLikeExternalPostList = (arr) => {
  if (!Array.isArray(arr) || arr.length < 1) return false;
  let tsHits = 0;
  for (const x of arr) {
    if (!x || typeof x !== 'object') return false;
    if (getPostTimestampRaw(x) != null) tsHits += 1;
  }
  if (tsHits === 0) return false;
  if (arr.length === 1) return tsHits === 1;
  return tsHits >= Math.max(2, Math.ceil(arr.length * 0.35));
};

const collectExternalPostArrays = (root, seen = new WeakSet(), out = []) => {
  if (root == null || typeof root !== 'object') return out;
  if (seen.has(root)) return out;
  seen.add(root);
  if (Array.isArray(root)) {
    if (arrayLooksLikeExternalPostList(root)) out.push(root);
    for (const el of root) collectExternalPostArrays(el, seen, out);
    return out;
  }
  for (const v of Object.values(root)) collectExternalPostArrays(v, seen, out);
  return out;
};

const extractExternalArchiveTextPosts = (data) => {
  const arrays = collectExternalPostArrays(data);
  const posts = [];
  let skippedMedia = 0;
  const seenTextAt = new Set();
  for (const arr of arrays) {
    for (const item of arr) {
      if (!item || typeof item !== 'object') continue;
      const ts = getPostTimestampRaw(item);
      if (ts == null) continue;
      if (isLikelyMediaOnlyPost(item)) {
        skippedMedia += 1;
        continue;
      }
      const text = getPostCaptionText(item);
      if (!text) {
        skippedMedia += 1;
        continue;
      }
      const date = timestampToIsoDate(ts);
      if (!date || !isDateKey(date)) continue;
      const dedupeKey = `${date}\n${text.slice(0, 200)}`;
      if (seenTextAt.has(dedupeKey)) continue;
      seenTextAt.add(dedupeKey);
      posts.push({ date, text });
    }
  }
  return { posts, skippedMedia };
};

export const Export = () => {
  const navigate = useNavigate();
  const today = new Date();
  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);
  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 30);

  const [dateRange, setDateRange] = useState('7');
  const [customStart, setCustomStart] = useState(last7Days);
  const [customEnd, setCustomEnd] = useState(today);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importText, setImportText] = useState('');

  const getDateRange = () => {
    if (dateRange === '7') {
      return { start: formatDate(last7Days), end: formatDate(today) };
    }
    if (dateRange === '30') {
      return { start: formatDate(last30Days), end: formatDate(today) };
    }
    return { start: formatDate(customStart), end: formatDate(customEnd) };
  };

  const handleExportCSV = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { start, end } = getDateRange();
      const result = await exportCSV(start, end);
      if (!result.native) {
        toast.success('CSV exported successfully');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { start, end } = getDateRange();
      const result = await exportPDF(start, end);
      if (!result.native) {
        toast.success('PDF exported successfully');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackup = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const data = exportAllData();
      const result = await exportBackup(data);
      if (!result.native) {
        toast.success('Backup file created');
      }
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to create backup');
    } finally {
      setIsExporting(false);
    }
  };

  const appendCloudAndSeedMessage = async (mergedEntries, baseMsg) => {
    const syncPayload = {};
    for (const d of Object.keys(mergedEntries || {})) {
      if (isDateKey(d) && mergedEntries[d]) syncPayload[d] = mergedEntries[d];
    }
    const syncResult =
      Object.keys(syncPayload).length > 0
        ? await syncJournalEntriesToCloudBulk(syncPayload)
        : null;
    const seeded = await seedSummaryHierarchyFromEntries(
      Object.entries(mergedEntries)
        .filter(([date]) => isDateKey(date))
        .map(([date, entry]) => ({ date, entry }))
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    );

    let msg = baseMsg;
    if (syncResult) {
      if (syncResult.skipped) {
        msg += ' Sign in to sync journal to the cloud for AI memory.';
      } else if (syncResult.failed > 0) {
        msg += ` Cloud sync: ${syncResult.synced} ok, ${syncResult.failed} failed — check connection.`;
      } else {
        msg += ` Cloud: uploaded ${syncResult.synced} journal day${syncResult.synced === 1 ? '' : 's'} touched by this import (not the whole library).`;
      }
    }
    if (seeded?.ok && seeded.generated > 0) {
      msg += ` Seeded ${seeded.generated} missing weekly/monthly History summary cache${seeded.generated === 1 ? '' : 's'} for AI.`;
    }

    toast.success(msg);

    const allRows = Object.entries(mergedEntries)
      .filter(([date]) => isDateKey(date))
      .map(([date, entry]) => ({ date, entry }))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    if (allRows.length >= 60) {
      setTimeout(() => {
        void import('@/utils/aiCompanion').then(({ generateHistorySummary }) => {
          void generateHistorySummary('yearly', allRows.slice(0, 365), {}).catch(() => {});
        });
      }, 4000);
    }
  };

  const importJsonObject = async (data, sourceFileName = '') => {
    if (isRebuildMeNativeBackup(data)) {
      setIsImporting(true);
      try {
        importAllData(data);
        const mergedEntries = getEntries() || {};
        await appendCloudAndSeedMessage(mergedEntries, 'Restored your journal from backup.');
      } catch (error) {
        console.error('Native JSON import error:', error);
        toast.error('Import failed. Backup file may be damaged or incompatible.');
      } finally {
        setIsImporting(false);
      }
      return;
    }

    const ext = extractExternalArchiveTextPosts(data);
    if (ext.posts.length > 0) {
      setIsImporting(true);
      try {
        saveImportedEntries(ext.posts);
        const mergedEntries = getEntries() || {};
        const n = ext.posts.length;
        const skipped = ext.skippedMedia;
        const lowerName = String(sourceFileName || '').toLowerCase();
        const instagramish = lowerName.includes('instagram') || lowerName.includes('insta_');
        const sourcePhrase = instagramish ? 'your Instagram archive' : 'your archive';
        let base = `Imported ${n} text memor${n === 1 ? 'y' : 'ies'} from ${sourcePhrase}`;
        if (skipped > 0) {
          base += instagramish ? ' (photos were skipped).' : ` (${skipped} media-only post${skipped === 1 ? '' : 's'} were skipped).`;
        } else {
          base += '.';
        }
        await appendCloudAndSeedMessage(mergedEntries, base);
      } catch (error) {
        console.error('External JSON import error:', error);
        toast.error('Import failed. Please try again.');
      } finally {
        setIsImporting(false);
      }
      return;
    }

    toast.error(
      'Unrecognized JSON. Use a RE-BUILD-ME-PRO backup file, or an archive with dated text posts (e.g. captions with timestamps).',
    );
  };

  const handleImportNotes = async (rawText) => {
    const text = String(rawText || '').trim();
    if (!text) {
      toast.error('Paste text or choose a file first.');
      return;
    }

    setIsImporting(true);
    try {
      const parsedEntries = splitImportedTextIntoEntries(text);
      if (parsedEntries.length > 0) {
        saveImportedEntries(parsedEntries);
        await seedSummaryHierarchyFromEntries(
          parsedEntries.map((row) => ({ date: row.date, entry: getEntry(row.date) || {} }))
            .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
        );
        toast.success(`Imported ${parsedEntries.length} dated entr${parsedEntries.length === 1 ? 'y' : 'ies'} into History.`);
      } else {
        const fallbackDate = new Date();
        fallbackDate.setDate(fallbackDate.getDate() - 1);
        const historicalDate = formatDate(fallbackDate);
        saveImportedEntries([{ date: historicalDate, text }]);
        await seedSummaryHierarchyFromEntries([{ date: historicalDate, entry: getEntry(historicalDate) || {} }]);
        toast.success('Imported notes saved as a historical entry in History.');
      }

      setImportText('');
    } catch (error) {
      console.error('Import notes error:', error);
      toast.error('Import failed. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = String(e.target?.result || '');
      const isJsonFile = String(file.name || '').toLowerCase().endsWith('.json');

      if (isJsonFile) {
        try {
          const data = JSON.parse(text);
          await importJsonObject(data, file.name || '');
        } catch {
          toast.error('Invalid JSON file');
        }
      } else {
        await handleImportNotes(text);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
            data-testid="back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
          </button>
          <h1 className="font-heading text-xl text-primary" data-testid="export-title">
            Import & Export
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <h2 className="font-body font-medium text-primary mb-3">Import</h2>
          <p className="font-body text-xs text-secondary mb-3 leading-relaxed">
            Paste any of your notes: if they include a full date they will be imported in date order. If they do not,
            they will be added to today’s log.
          </p>
          <p className="font-body text-xs text-secondary mb-3 leading-relaxed">
            You can import JSON from RE-BUILD-ME, another diary, or a social export — the app places journal text in
            date order where it can.
          </p>
          <p className="font-body text-xs text-secondary mb-3 leading-relaxed">
            For every JSON import, only text is brought in — not photos, videos, or other media.
          </p>
          <Textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={6}
            placeholder="Paste notes here..."
            className="bg-app border-theme-secondary resize-none mb-3"
            data-testid="import-textarea"
          />
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                void handleImportNotes(importText);
              }}
              disabled={isImporting}
              className="rounded-2xl px-4 py-3 bg-theme-primary text-primary font-body text-sm disabled:opacity-50"
              data-testid="import-text-btn"
            >
              {isImporting ? 'Importing...' : 'Import Text'}
            </button>
            <label
              className={`rounded-2xl px-4 py-3 bg-theme-primary text-primary font-body text-sm text-center cursor-pointer block ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}
            >
              Import File
              <input
                type="file"
                accept=".txt,.md,.csv,.json"
                onChange={handleImportFile}
                disabled={isImporting}
                className="hidden"
                data-testid="import-file-input"
              />
            </label>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <h2 className="font-body font-medium text-primary mb-4">Export</h2>

          <div className="space-y-3 mb-4">
            <button
              onClick={() => setDateRange('7')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === '7'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-7-days"
            >
              Last 7 days
            </button>

            <button
              onClick={() => setDateRange('30')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === '30'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-30-days"
            >
              Last 30 days
            </button>

            <button
              onClick={() => setDateRange('custom')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === 'custom'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-custom"
            >
              Custom range
            </button>
          </div>

          {dateRange === 'custom' && (
            <div className="mt-4 flex gap-3 mb-4">
              <Popover open={showStartPicker} onOpenChange={setShowStartPicker}>
                <PopoverTrigger asChild>
                  <button
                    className="flex-1 p-3 rounded-xl bg-wisteria-50/50 text-left text-sm font-body text-secondary flex items-center gap-2"
                    data-testid="custom-start-date"
                  >
                    <Calendar size={16} />
                    {formatShortDate(formatDate(customStart))}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={customStart}
                    onSelect={(date) => {
                      if (date) {
                        setCustomStart(date);
                        setShowStartPicker(false);
                      }
                    }}
                    disabled={(date) => date > today}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <span className="self-center text-secondary">to</span>

              <Popover open={showEndPicker} onOpenChange={setShowEndPicker}>
                <PopoverTrigger asChild>
                  <button
                    className="flex-1 p-3 rounded-xl bg-wisteria-50/50 text-left text-sm font-body text-secondary flex items-center gap-2"
                    data-testid="custom-end-date"
                  >
                    <Calendar size={16} />
                    {formatShortDate(formatDate(customEnd))}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={customEnd}
                    onSelect={(date) => {
                      if (date) {
                        setCustomEnd(date);
                        setShowEndPicker(false);
                      }
                    }}
                    disabled={(date) => date > today || date < customStart}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-csv-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <FileText size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Exporting CSV. Please wait.' : 'Export as CSV. Spreadsheet format.'}
              </p>
            </button>

            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-pdf-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <Sparkles size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Exporting PDF. Please wait.' : 'Export as PDF. Printable document.'}
              </p>
            </button>

            <button
              onClick={handleBackup}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="backup-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <Upload size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Creating backup. Please wait.' : 'Create backup. Export all data as JSON.'}
              </p>
            </button>
          </div>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default Export;